import { Injectable } from "@angular/core";
import { JwtHelperService } from "@auth0/angular-jwt";
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: "root",
})
export class AccessTokenService {
  private static readonly SESSION_STORAGE_KEY_JWT: string = 'JWT';
  // When the backend guarantees the token will be valid for 15 minutes then we can refresh 5 minutes before the end and always get a new one.
  private static readonly TOKEN_REFRESH_THRESHOLD_MS: number = 5 * 60 * 1000;
  private static readonly TOKEN_MIN_REFRESH_INTERVAL_MS: number = 10 * 1000;

  private readonly accessTokenSubject = new BehaviorSubject<string | null>(null);
  private readonly jwtHelper = new JwtHelperService();

  public refreshToken: ((oldToken: string | null) => void) | null = null;
  private refreshTimer: number | null = null;

  constructor() {
    const token = this.tryLoadFromSessionStorage();
    if (token) {
      this.setAccessToken(token);
    }
  }

  public get accessToken(): Observable<string | null> {
    return this.accessTokenSubject;
  }

  public get currentAccessToken(): string | null {
    if (!this.accessTokenSubject.value) {
      const token = this.tryLoadFromSessionStorage();
      if (token) {
        this.setAccessToken(token);
      }
    }

    return this.accessTokenSubject.value;
  }

  public setAccessToken(newAccessToken?: string) {
    if (!newAccessToken || !this.isTokenValid(newAccessToken)) {
      return false;
    }

    this.accessTokenSubject.next(newAccessToken);
    sessionStorage.setItem(AccessTokenService.SESSION_STORAGE_KEY_JWT, newAccessToken);

    // Set up a timer to request a token refresh before it expires
    const expirationDate: Date = this.jwtHelper.getTokenExpirationDate(newAccessToken) ?? new Date(0);
    const timeUntilExpiration: number = expirationDate.getTime() - Date.now();
    const timeUntilRefresh: number = Math.max(AccessTokenService.TOKEN_MIN_REFRESH_INTERVAL_MS, timeUntilExpiration - AccessTokenService.TOKEN_REFRESH_THRESHOLD_MS);

    if (this.refreshTimer !== null) {
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    this.refreshTimer = window.setTimeout(() => {
      if (this.refreshToken) {
        this.refreshToken(this.accessTokenSubject.value);
      }
    }, timeUntilRefresh);

    return true;
  }

  public isTokenValid(token?: string) {
    if (!token) {
      return false;
    }

    try {
      // JWT should have three parts
      const parts = token.split('.');
      if (parts.length !== 3) {
        return false;
      }

      // Header should have an algorithm specified for signature check
      const header: { alg: string } = JSON.parse(this.jwtHelper.urlBase64Decode(parts[0]));
      if (!header.alg) {
        return false;
      }

      // Body should have at least some data
      const body: any = JSON.parse(this.jwtHelper.urlBase64Decode(parts[1]));
      if (!Object.keys(body).length) {
        return false;
      }

      // Cannot parse signature because it is binary data, not text, don't check it

      // Expired tokens are not useful
      if (this.jwtHelper.isTokenExpired(token, 0)) {
        return false;
      }

      // Probably a valid token
      return true;
    }
    catch {
      return false;
    }
  }

  private tryLoadFromSessionStorage() : string | null {
    const jwt = sessionStorage.getItem(AccessTokenService.SESSION_STORAGE_KEY_JWT);
    if (jwt !== null) {
      if (!this.isTokenValid(jwt)) {
        // Invalid token!
        sessionStorage.removeItem(AccessTokenService.SESSION_STORAGE_KEY_JWT);
        return null;
      }
    }

    return jwt;
  }
}
